/*
 * File: d:\projects\AutoFiFront\src\Services\Users\UserService.ts
 * Project: d:\projects\AutoFiFront
 * Created Date: Friday January 13th 2023
 * Author: Jonathan Mazat
 * -----
 * Last Modified: Friday January 13th 2023 2:10:45 pm
 * Modified By: the developer formerly known as Jonathan Mazat at <you@you.you>
 * -----
 * Copyright (c) 2023 Autobuy SAS
 * -----
 * HISTORY:
 */

import Axios from "axios";
import Config from "Config/Config";
import UserSession from "Utils/UserSession";
import AuthenticationError from "Error/AuthenticationError";
import qs from "qs";
import ServiceGeneral from "Services/ServiceGeneral";

/**
 * Manage user operations with the backend
 */
class UserService {
  /**
   * Login a user with the given credentials
   * @param {string} username The username
   * @param {string} password The password
   * @returns Promise with info of tokens if login successful
   * @throws Throws if an error happened when reaching back
   */
  static async login(username: string, password: string, refresh: boolean) {
    const url = Config.BACKEND.BASEURL + Config.BACKEND.PATHS.LOGIN + "/login";
    const params = qs.stringify({
      grant_type: "password",
      username: username,
      password: password,
      refresh: refresh
    });

    try {
      // Set the tokens in cookies for future auth through middleware
      const response = await Axios.post(url, params);
      if (response.status !== 200) return response;

      UserSession.setTokens({
        access_token: response.data.token.access_token,
        refresh_token: response.data.token.refresh,
      });

      return response;
    } catch (e) {
      if (e instanceof AuthenticationError)
        throw e;
      const data = ServiceGeneral.interpretError(e);
      return data;
    }
  }

  static async getMe() {
    const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.GET_USER}${Config.BACKEND.PATHS.SUMMARY}`;
    return ServiceGeneral.manageCall(url, "get");
  }
  static async getMyProfile() {
    const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.GET_USER}${Config.BACKEND.PATHS.PROFILE}`;
    const myRet = await ServiceGeneral.manageCall(url, "get");
    return myRet.data;
  }

  static updateUser(userID: string, data: {[key: string]: string | Date | number | undefined }) {
    const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.GET_USER}/${userID}`;
    return ServiceGeneral.manageCall(url, "put", data);
  }

  static async addUser(user: any) {
    const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.ADD_USER}`;

    try {
      const response = await Axios.post(url, user);
      if ((await ServiceGeneral.handleAxiosResponse(response)) === "end") {
        return undefined;
      }
      return response;
    } catch (e) {
      if (e instanceof AuthenticationError) throw e;
      const data = ServiceGeneral.interpretError(e);
      return data;
    }
  }

  static async setNewPassword(password: string, encrypted: string) {
    const url =
      Config.BACKEND.BASEURL +
      Config.BACKEND.PATHS.GET_USER +
      "/new_password/" +
      encrypted;
    const tokens = UserSession.getAccessToken();
    const data = {
      headers: {},
    };

    if (tokens !== undefined) {
      data.headers = {
        Authorization: tokens.token_type + " " + tokens.access_token,
      };
    }
    try {
      const response = await Axios.post(url, { password: password }, data);
      if ((await ServiceGeneral.handleAxiosResponse(response)) === "end") {
        return undefined;
      }
      return response;
    } catch (e) {
      if (e instanceof AuthenticationError) throw e;
      const data = ServiceGeneral.interpretError(e);
      return data;
    }
  }

  static async forgottenPassword(userMail: string) {
    const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.GET_USER}/password`;
    try {
      const response = await Axios.post(url, { userMail });
      if ((await ServiceGeneral.handleAxiosResponse(response)) === "end") {
        return undefined;
      }
      return response;
    } catch (e) {
      if (e instanceof AuthenticationError) throw e;
      const data = ServiceGeneral.interpretError(e);
      return data;
    }
  }

  static async setPasswordReneval(password: string, token: string) {
    const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.GET_USER}/password_renewal`;
    return Axios.put(url, { password, token });
  }

  static updatePassword(curPwd: string, newPwd: string) {
    const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.GET_USER}/password`;
    return ServiceGeneral.manageCall(url, "put", {current: curPwd, new: newPwd});
  }

  static async verify(token: string) {
    const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.GET_USER}/verify_user`;
    const myRet = await ServiceGeneral.manageCall(url, "post", {token});
    return myRet.status;
  }

}

export default UserService;
